import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Drives sections/collection-bundle-pricing.liquid. Applies the same
 * cart-quantity-based tier logic as assets/bundle-and-save-v2.js (1 item in
 * cart -> tier 1, 2 -> tier 2, 3+ -> tier 3) to the standard shared Price
 * blocks already rendered on this page's product cards, found via
 * `product-price[data-product-id]` (an attribute blocks/price.liquid already
 * renders) rather than by editing that shared block/snippet/component.
 *
 * Only items that actually qualify for the Bundle & Save automatic discounts
 * count toward the tier, and only qualifying products get a discounted price
 * shown. Eligibility comes from the `eligibleData` ref — the product IDs in
 * Theme settings > Bundle deal bar > eligible collections, rendered by
 * snippets/bundle-eligible-product-ids.liquid — because /cart.js line items
 * don't carry their product's collections. That's the same rule
 * snippets/cart-bundle-bar.liquid applies server-side, so the card preview, the
 * cart bundle bar and the real discount all agree on what counts. When no
 * eligible collections are configured the list is empty and this falls back to
 * counting the whole cart.
 *
 * @typedef {{price: number, compareAtPrice: number|null}} BundlePriceInfo
 * @typedef {object} Refs
 * @property {HTMLScriptElement} priceData
 * @property {HTMLScriptElement} [eligibleData]
 * @property {HTMLTemplateElement} badgeTemplate
 * @extends {Component<Refs>}
 */
class CollectionBundlePrice extends Component {
  requiredRefs = ['priceData', 'badgeTemplate'];

  /** @type {number[]} */
  #tierPercents = [10, 15, 20];

  /** @type {Record<string, BundlePriceInfo>} */
  #priceMap = {};

  /** @type {Set<string>} */
  #eligibleProductIds = new Set();

  /** @type {Map<Element, {priceEl: Element, originalPriceText: string}>} */
  #entries = new Map();

  /**
   * Server-rendered price text, captured the first time each price element is
   * seen. Kept outside #entries, which is rebuilt from the live DOM on every
   * cart update — reading the "original" text back out of an element this
   * component has already discounted would bake the discounted value in as the
   * original, leaving it stuck there once the cart empties again.
   * @type {WeakMap<Element, string>}
   */
  #originalPriceText = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    const percents = (this.dataset.tierPercents || '')
      .split(',')
      .map((value) => parseInt(value, 10))
      .filter((value) => !isNaN(value));
    if (percents.length === 3) this.#tierPercents = percents;

    try {
      this.#priceMap = JSON.parse(this.refs.priceData.textContent ?? '{}');
    } catch (error) {
      this.#priceMap = {};
    }

    try {
      const eligibleIds = JSON.parse(this.refs.eligibleData?.textContent ?? '[]');
      this.#eligibleProductIds = new Set(
        (Array.isArray(eligibleIds) ? eligibleIds : []).map((id) => String(id))
      );
    } catch (error) {
      this.#eligibleProductIds = new Set();
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#onCartUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /**
   * With no eligible collections configured there's nothing to filter on, so
   * every product and every cart line is treated as qualifying — the behavior
   * this had before eligibility was taken into account.
   * @returns {boolean}
   */
  get #hasEligibilityData() {
    return this.#eligibleProductIds.size > 0;
  }

  /**
   * @param {string|number|null|undefined} productId
   * @returns {boolean}
   */
  #isEligible(productId) {
    if (!this.#hasEligibilityData) return true;
    return productId != null && this.#eligibleProductIds.has(String(productId));
  }

  #collectEntries() {
    this.#entries.clear();
    document.querySelectorAll('product-price[data-product-id]').forEach((el) => {
      const productId = el.getAttribute('data-product-id');
      if (!productId || !(productId in this.#priceMap)) return;

      const priceEl = el.querySelector('.price');
      if (!priceEl) return;

      if (!this.#originalPriceText.has(priceEl)) {
        this.#originalPriceText.set(priceEl, priceEl.textContent ?? '');
      }

      if (!this.#entries.has(el)) {
        this.#entries.set(el, { priceEl, originalPriceText: this.#originalPriceText.get(priceEl) ?? '' });
      }
    });
  }

  #onCartUpdate = () => {
    this.#fetchEligibleCartQuantity().then((quantity) => this.#applyTier(quantity));
  };

  /**
   * Total quantity of the cart lines whose product qualifies for Bundle & Save.
   * Ineligible lines (a Flow Star, say) are ignored, so adding one no longer
   * moves the tier — matching the real automatic discount and the cart bar.
   * @returns {Promise<number>}
   */
  async #fetchEligibleCartQuantity() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      if (!this.#hasEligibilityData) return cart.item_count ?? 0;

      return (cart.items ?? []).reduce(
        (total, item) => (this.#isEligible(item?.product_id) ? total + (item?.quantity ?? 0) : total),
        0
      );
    } catch (error) {
      return 0;
    }
  }

  /**
   * @param {number} quantity
   * @returns {number}
   */
  #getTierPercent(quantity) {
    if (quantity >= 3) return this.#tierPercents[2] ?? 0;
    if (quantity === 2) return this.#tierPercents[1] ?? 0;
    if (quantity >= 1) return this.#tierPercents[0] ?? 0;
    return 0;
  }

  /** @param {number} quantity */
  #applyTier(quantity) {
    // Product cards can be re-rendered (pagination, filtering, sorting) after
    // this component connects, so re-collect on every cart update rather than
    // caching once.
    this.#collectEntries();

    const tierPercent = this.#getTierPercent(quantity);
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    this.#entries.forEach(({ priceEl, originalPriceText }, productPriceEl) => {
      const productId = productPriceEl.getAttribute('data-product-id');
      const priceInfo = productId ? this.#priceMap[productId] : null;
      if (!priceInfo) return;

      let compareEl = productPriceEl.querySelector('.compare-at-price');
      let badge = productPriceEl.querySelector('.bundle-and-save__badge');

      // A card for a product the bundle discount doesn't cover keeps its normal
      // price no matter what's in the cart.
      const showDiscount = tierPercent > 0 && this.#isEligible(productId);

      if (showDiscount) {
        const discountedPrice = Math.round(priceInfo.price * (1 - tierPercent / 100));
        priceEl.textContent = formatMoney(discountedPrice, format, currency);

        if (!compareEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'compare-at-price';
          priceEl.insertAdjacentElement('afterend', compareEl);
        }
        compareEl.textContent = formatMoney(priceInfo.price, format, currency);
        compareEl.hidden = false;

        if (!badge) {
          const clone = this.refs.badgeTemplate.content.cloneNode(true);
          compareEl.insertAdjacentElement('afterend', /** @type {DocumentFragment} */ (clone).firstElementChild);
          badge = productPriceEl.querySelector('.bundle-and-save__badge');
        }
        if (badge) badge.hidden = false;
      } else {
        priceEl.textContent = originalPriceText;

        if (compareEl) {
          if (priceInfo.compareAtPrice) {
            compareEl.textContent = formatMoney(priceInfo.compareAtPrice, format, currency);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }

        if (badge) badge.hidden = true;
      }
    });
  }
}

if (!customElements.get('collection-bundle-price')) {
  customElements.define('collection-bundle-price', CollectionBundlePrice);
}
